import { PatternCoordinateMap } from "./pattern-coordinate-map";
import type { AssertionDefinition, Bounds, ClipDefinition, Diagnostic, EdgeDefinition, DraftAssertion, DraftBlock, DraftEdge, DraftInlay, DraftMark, DraftReferenceOverlay, DraftResult, DraftSeamAllowance, DraftSegment, MarkDefinition, ReferenceOverlayDefinition, PatternProject, PatternSpecification, PointTuple } from "./types";
import { cutLineForLoop } from "./cut-line";
import { clipOutline } from "./clip";
import { chainEdges, compileEdge, DERIVED_EDGE_OPERATIONS, edgeCrossing, edgePortion, edgesCross, mirrorSegments, pointAlongEdges, sampleSegment } from "./geometry";
import { buildMannequin } from "./mannequin";
import { activeSpecification } from "./conditions";
import { layoutPrint } from "./print-layout";
import { expandTransforms, transformSegments } from "./transforms";
import type { OptionValue } from "./types";

/** Segments for an edge drafted from other drafted edges; undefined when its inputs are missing. */
function derivedSegments(edge: EdgeDefinition, blockId: string, sources: DraftEdge[], points: Record<string, PointTuple>, generator: PatternCoordinateMap | undefined): DraftSegment[] | undefined {
  const from = points[edge.from];
  const to = points[edge.to];
  if (edge.operation === "edgePortion") {
    const run = chainEdges(sources.map((source) => source.segments));
    return run && edgePortion(run, from, to);
  }
  if (edge.operation === "mirrorEdge") {
    const [axisStart, axisEnd] = (edge.axis ?? []).map((name) => points[name]);
    return axisStart && axisEnd ? mirrorSegments(sources[0].segments, axisStart, axisEnd, from, to) : undefined;
  }
  if (edge.operation === "transformEdge") {
    if (!generator || !edge.transform) return undefined;
    return transformSegments(sources[0].segments, generator.transformMatrix(blockId, edge.transform), from, to);
  }
  return undefined;
}

const EMPTY_BOUNDS: Bounds = { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };

export function boundsFor(points: PointTuple[]): Bounds {
  if (points.length === 0) return EMPTY_BOUNDS;
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

export function draftPattern(source: PatternSpecification, project: PatternProject): DraftResult {
  // parseSpecification has usually written transforms out already; this is a no-op then.
  const authored = expandTransforms(source);
  const diagnostics: Diagnostic[] = [];
  let points: Record<string, PointTuple> = {};
  let generator: PatternCoordinateMap | undefined;
  // Elements switched off by `when` are dropped before anything is drawn.
  let specification = authored;
  try {
    generator = new PatternCoordinateMap(authored, {
      measurements: project.measurements,
      options: project.options,
    });
    points = generator.generate({ strict: false });
    // A `when` may read points placed from derived values, so the elements
    // that are on are worked out again after each pass; only the last pass
    // reports conditions that still cannot be resolved.
    const active = (map: PatternCoordinateMap, report: Diagnostic[]) => activeSpecification(authored, (expression) => map.resolve({ expression }), report);
    specification = active(generator, []);
    // Derived values read drafted geometry, and points can read derived values,
    // so a derived value may need points that only exist after an earlier
    // pass. Repeat until nothing changes (see MAX_DERIVED_PASSES).
    let derived: Record<string, number> = {};
    for (let pass = 0; pass < MAX_DERIVED_PASSES; pass += 1) {
      const next = resolveGeometryDerivedValues(specification, points, generator);
      if (sameValues(next, derived)) break;
      derived = next;
      generator = new PatternCoordinateMap(authored, {
        measurements: project.measurements,
        options: project.options,
        derived,
      });
      points = generator.generate({ strict: false });
      specification = active(generator, []);
    }
    specification = active(generator, diagnostics);
  } catch (error) {
    diagnostics.push({ severity: "error", code: "DRAFT_FAILED", message: error instanceof Error ? error.message : "Draft failed." });
    if (specification === authored) specification = activeSpecification(authored, undefined, []);
  }

  const edges: DraftEdge[] = [];
  for (const [blockId, block] of Object.entries(specification.blocks)) {
    for (const [edgeId, edge] of Object.entries(block.edges ?? {})) {
      if (DERIVED_EDGE_OPERATIONS.has(edge.operation)) continue;
      if (!points[edge.from] || !points[edge.to]) {
        diagnostics.push({ severity: "warning", code: "EDGE_UNRESOLVED", message: `${blockId}.${edgeId} could not be drawn.` });
        continue;
      }
      const id = `${blockId}.${edgeId}`;
      try {
        edges.push({ id, blockId, from: edge.from, to: edge.to, operation: edge.operation, segments: compileEdge(edge, points) });
      } catch (error) {
        diagnostics.push({ severity: "warning", code: "EDGE_INVALID", message: `${blockId}.${edgeId}: ${error instanceof Error ? error.message : "Invalid geometry."}` });
        continue;
      }
    }
  }

  // Edges drafted from other edges: portions (the stretch of a side seam a
  // yoke keeps), mirror images (a facing folded out beyond a fold line) and
  // moved copies (a dart transfer or slash and spread). One may build on
  // another, so resolve them in passes until nothing more can be drawn.
  const pending = Object.entries(specification.blocks).flatMap(([blockId, block]) =>
    Object.entries(block.edges ?? {}).filter(([, edge]) => DERIVED_EDGE_OPERATIONS.has(edge.operation)).map(([edgeId, edge]) => ({ blockId, edgeId, edge })));
  const drafted = (id: string) => edges.find((candidate) => candidate.id === id);
  // A derived edge's sources are in its own block first, then in any block
  // (a sleeve block's head can be cut to join a body section).
  const sourceBlock = (blockId: string, name: string) => specification.blocks[blockId]?.edges?.[name]
    ? blockId
    : Object.keys(specification.blocks).find((other) => specification.blocks[other].edges?.[name]) ?? blockId;
  while (pending.length > 0) {
    const waiting: typeof pending = [];
    for (const item of pending) {
      const { blockId, edgeId, edge } = item;
      const names = edge.operation === "edgePortion" ? edge.edges ?? (edge.edge ? [edge.edge] : []) : edge.edge ? [edge.edge] : [];
      const sources = names.map((name) => drafted(`${sourceBlock(blockId, name)}.${name}`));
      // A source still to be drawn may come in a later pass.
      if (names.length > 0 && sources.some((source) => !source) && names.some((name) => pending.some((other) => other !== item && other.blockId === sourceBlock(blockId, name) && other.edgeId === name))) {
        waiting.push(item);
        continue;
      }
      const id = `${blockId}.${edgeId}`;
      const from = points[edge.from];
      const to = points[edge.to];
      if (names.length === 0 || sources.some((source) => !source) || !from || !to) {
        diagnostics.push({ severity: "warning", code: "EDGE_UNRESOLVED", message: `${id} could not be drawn.` });
        continue;
      }
      try {
        const segments = derivedSegments(edge, blockId, sources as DraftEdge[], points, generator);
        if (!segments) {
          diagnostics.push({ severity: "warning", code: "EDGE_UNRESOLVED", message: `${id} could not be drawn.` });
          continue;
        }
        edges.push({ id, blockId, from: edge.from, to: edge.to, operation: edge.operation, segments });
      } catch (error) {
        diagnostics.push({ severity: "warning", code: "EDGE_INVALID", message: `${id}: ${error instanceof Error ? error.message : "Invalid geometry."}` });
      }
    }
    if (waiting.length === pending.length) {
      for (const { blockId, edgeId } of waiting) diagnostics.push({ severity: "warning", code: "EDGE_UNRESOLVED", message: `${blockId}.${edgeId} could not be drawn: its source edges depend on each other.` });
      break;
    }
    pending.splice(0, pending.length, ...waiting);
  }

  const seamComparisons = Object.entries(specification.seamComparisons ?? {}).flatMap(([id, definition]) => {
    const firstEdges = definition.first.edges.map((edgeId) => edges.find((edge) => edge.id === edgeId));
    const secondEdges = definition.second.edges.map((edgeId) => edges.find((edge) => edge.id === edgeId));
    const missing = [
      ...definition.first.edges.filter((_, index) => !firstEdges[index]),
      ...definition.second.edges.filter((_, index) => !secondEdges[index]),
    ];
    if (missing.length > 0) {
      diagnostics.push({ severity: "warning", code: "SEAM_COMPARISON_UNRESOLVED", message: `${definition.label} could not measure: ${missing.join(", ")}.` });
      return [];
    }
    const firstLength = firstEdges.reduce((sum, edge) => sum + edgeLength(edge!), 0);
    const secondLength = secondEdges.reduce((sum, edge) => sum + edgeLength(edge!), 0);
    const tolerance = definition.tolerance ?? 1;
    const difference = secondLength - firstLength;
    return [{
      id,
      label: definition.label,
      first: { label: definition.first.label, edgeIds: definition.first.edges, length: firstLength },
      second: { label: definition.second.label, edgeIds: definition.second.edges, length: secondLength },
      difference,
      tolerance,
      matches: Math.abs(difference) <= tolerance,
    }];
  });

  const marks: DraftMark[] = [];
  for (const [blockId, block] of Object.entries(specification.blocks)) {
    for (const mark of block.marks ?? []) {
      const compiled = compileMark(mark, blockId, points, edges, project.options);
      if (compiled) marks.push(compiled);
      else diagnostics.push({ severity: "warning", code: "MARK_UNRESOLVED", message: `${blockId}.${mark.id} could not be drawn.` });
    }
  }

  // Construction blocks may contain several finished pattern pieces. Layout
  // those pieces independently while retaining the shared construction points.
  const blocks: DraftBlock[] = [];
  const seamAllowances: DraftSeamAllowance[] = [];
  const inlays: DraftInlay[] = [];
  let cursorX = 0;
  for (const [blockId, block] of Object.entries(specification.blocks)) {
    const pieces = Object.entries(block.pieces ?? {});
    // activeSpecification has flattened every edge and mark list to plain IDs.
    type Flat<T> = Omit<T, "edges"> & { edges: string[] };
    type Piece = NonNullable<typeof block.pieces>[string];
    const layouts = pieces.length > 0
      ? pieces.map(([pieceId, piece]) => ({ id: `${blockId}.${pieceId}`, layoutWith: piece.layoutWith ? `${blockId}.${piece.layoutWith}` : undefined, label: piece.name, bodyOverlayRule: piece.bodyOverlay as { label?: string; edges: string[] } | undefined, overlayRules: (piece.referenceOverlays ?? []) as Flat<ReferenceOverlayDefinition>[], edgeNames: piece.outline as string[], internalLineNames: (piece.internalLines ?? []) as string[], markNames: (piece.internalMarks ?? []) as string[], allowanceRules: (piece.seamAllowances ?? []) as Flat<NonNullable<Piece["seamAllowances"]>[number]>[], inlayRules: (piece.inlays ?? []) as Flat<NonNullable<Piece["inlays"]>[number]>[], clipRules: (piece.clips ?? []) as Array<Omit<ClipDefinition, "along"> & { along: string[] }>, cutQuantity: piece.print?.cutQuantity ?? 1, grainDirection: piece.print?.grainDirection ?? "vertical" as const, materialRole: piece.print?.materialRole as string | undefined, cutOnFold: piece.print?.cutOnFold ? `${blockId}.${piece.print.cutOnFold}` : undefined }))
      // A block whose pieces are all switched off has nothing to lay out.
      : authored.blocks[blockId]?.pieces && Object.keys(authored.blocks[blockId].pieces!).length > 0 ? []
      : [{ id: blockId, layoutWith: undefined as string | undefined, label: block.label ?? blockId, bodyOverlayRule: undefined as { label?: string; edges: string[] } | undefined, overlayRules: [], edgeNames: Object.keys(block.edges ?? {}), internalLineNames: [] as string[], markNames: (block.marks ?? []).map((mark) => mark.id), allowanceRules: [], inlayRules: [], clipRules: [], cutQuantity: 1, grainDirection: "vertical" as const, materialRole: undefined as string | undefined, cutOnFold: undefined as string | undefined }];

    const firstPieceIndex = blocks.length;
    for (const layout of layouts) {
      if (layout.clipRules.length > 0) clipPiece(layout, blockId, edges, points, diagnostics);
      const edgeIds = layout.edgeNames.map((edgeId) => `${blockId}.${edgeId}`);
      const markIds = layout.markNames.map((markId) => `${blockId}.${markId}`);
      const pieceEdges = edges.filter((edge) => edgeIds.includes(edge.id));
      const pieceMarks = marks.filter((mark) => markIds.includes(mark.id));
      // Include construction points referenced by marks so the working is visible.
      const markPointIds = (block.marks ?? [])
        .filter((mark) => layout.markNames.includes(mark.id) && mark.operation !== "distributeButtons")
        .flatMap((mark) => [mark.from, mark.to, mark.at].filter((id): id is string => !!id && !!points[id]));
      const pointIds = [...new Set([...pieceEdges.flatMap((edge) => [edge.from, edge.to]), ...markPointIds])];
      const outlinePoints = pieceEdges.flatMap((edge) => edge.segments.flatMap((segment) => sampleSegment(segment)));
      const centre = centroid(outlinePoints);
      const widths = new Map<string, number>();
      const includedEdgeIds = new Set<string>();
      for (const rule of layout.allowanceRules) {
        for (const edgeName of rule.edges) {
          widths.set(`${blockId}.${edgeName}`, resolveMarkValue(rule.distance, project.options));
          if (rule.included) includedEdgeIds.add(`${blockId}.${edgeName}`);
        }
      }
      // One cut line around the closed outline, so allowances meet cleanly at
      // corners. Included allowances get their own line offset inwards: the
      // stitch line, with the outline itself as the cut line.
      const outlineEdges = edgeIds.map((id) => edges.find((edge) => edge.id === id));
      const loop = widths.size > 0 && outlineEdges.every((edge): edge is DraftEdge => !!edge) ? orientLoop(outlineEdges) : undefined;
      const addedRuns = loop && widths.size > includedEdgeIds.size
        ? cutLineForLoop(loop.map((edge) => ({ ...edge, width: includedEdgeIds.has(edge.id) ? 0 : widths.get(edge.id) ?? 0 })))
        : undefined;
      const stitchRuns = loop && includedEdgeIds.size > 0
        ? cutLineForLoop(loop.map((edge) => ({ ...edge, width: includedEdgeIds.has(edge.id) ? -(widths.get(edge.id) ?? 0) : 0 })))
        : undefined;
      if (widths.size > 0 && !loop) {
        diagnostics.push({ severity: "warning", code: "SEAM_ALLOWANCE_OPEN_OUTLINE", message: `${layout.id} outline does not close, so its seam allowances are drawn edge by edge.` });
      }
      const pieceAllowances = layout.allowanceRules.flatMap((rule) => rule.edges.flatMap((edgeName) => {
        const edge = edges.find((candidate) => candidate.id === `${blockId}.${edgeName}`);
        if (!edge) {
          diagnostics.push({ severity: "warning", code: "SEAM_ALLOWANCE_UNRESOLVED", message: `${layout.id}.${edgeName} seam allowance could not be drawn.` });
          return [];
        }
        const distance = widths.get(edge.id) ?? 0;
        const included = includedEdgeIds.has(edge.id);
        const looped = loop?.find((candidate) => candidate.id === edge.id);
        const runs = included ? stitchRuns : addedRuns;
        const cut = runs && runFor(runs, edge.id);
        const allowance: DraftSeamAllowance = {
          id: `${layout.id}.seamAllowance.${edgeName}`,
          blockId,
          pieceId: layout.id,
          sourceEdgeId: edge.id,
          distance,
          included,
          polygon: looped && cut?.length
            ? [...cut, ...segmentPolyline(looped.segments).reverse()]
            : offsetStrip(edge, centre, distance, (rule.flip ?? false) !== included),
        };
        seamAllowances.push(allowance);
        return [allowance];
      }));
      const seamAllowanceIds = pieceAllowances.map((allowance) => allowance.id);
      // Inlay sits beyond the seam allowance: a second cut line, offset by the
      // allowance plus the inlay, with the strip between the two cut lines.
      const inlayWidths = new Map<string, number>();
      if (project.includeInlay) {
        for (const rule of layout.inlayRules) {
          for (const edgeName of rule.edges) inlayWidths.set(`${blockId}.${edgeName}`, resolveMarkValue(rule.distance, project.options));
        }
      }
      const addedWidth = (edgeId: string) => includedEdgeIds.has(edgeId) ? 0 : widths.get(edgeId) ?? 0;
      const inlayLoop = inlayWidths.size > 0 && outlineEdges.every((edge): edge is DraftEdge => !!edge) ? orientLoop(outlineEdges) : undefined;
      if (inlayWidths.size > 0 && !inlayLoop) {
        diagnostics.push({ severity: "warning", code: "INLAY_OPEN_OUTLINE", message: `${layout.id} outline does not close, so its inlays are drawn edge by edge.` });
      }
      const innerRuns = inlayLoop && cutLineForLoop(inlayLoop.map((edge) => ({ ...edge, width: addedWidth(edge.id) })));
      const outerRuns = inlayLoop && cutLineForLoop(inlayLoop.map((edge) => ({ ...edge, width: addedWidth(edge.id) + (inlayWidths.get(edge.id) ?? 0) })));
      const pieceInlays = layout.inlayRules.flatMap((rule) => rule.edges.flatMap((edgeName) => {
        const edge = edges.find((candidate) => candidate.id === `${blockId}.${edgeName}`);
        const distance = edge ? inlayWidths.get(edge.id) ?? 0 : 0;
        if (!edge || distance <= 0) {
          if (!edge && project.includeInlay) diagnostics.push({ severity: "warning", code: "INLAY_UNRESOLVED", message: `${layout.id}.${edgeName} inlay could not be drawn.` });
          return [];
        }
        let inner = innerRuns && runFor(innerRuns, edge.id);
        let outer = outerRuns && runFor(outerRuns, edge.id);
        if (!inner?.length || !outer?.length) {
          const base = addedWidth(edge.id);
          const source = segmentPolyline(edge.segments);
          inner = base > 0 ? offsetStrip(edge, centre, base, false).slice(source.length).reverse() : source;
          outer = offsetStrip(edge, centre, base + distance, false).slice(source.length).reverse();
        }
        const innerMid = inner[Math.floor(inner.length / 2)];
        const outerMid = outer[Math.floor(outer.length / 2)];
        const inlay: DraftInlay = {
          id: `${layout.id}.inlay.${edgeName}`,
          blockId,
          pieceId: layout.id,
          sourceEdgeId: edge.id,
          distance,
          polygon: [...outer, ...[...inner].reverse()],
          labelAt: [(innerMid[0] + outerMid[0]) / 2, (innerMid[1] + outerMid[1]) / 2],
        };
        inlays.push(inlay);
        return [inlay];
      }));
      const inlayIds = pieceInlays.map((inlay) => inlay.id);
      const geometryPoints = [...outlinePoints, ...pieceMarks.flatMap((mark) => mark.points), ...pieceAllowances.flatMap((allowance) => allowance.polygon), ...pieceInlays.flatMap((inlay) => inlay.polygon)];
      const bounds = boundsFor(geometryPoints);
      const offset: PointTuple = [cursorX - bounds.minX, -bounds.minY];
      const overlayMoves = new Map<string, (point: PointTuple) => PointTuple>();
      const referenceOverlays = layout.overlayRules.flatMap((rule) => {
        const source = layouts.find((candidate) => candidate.edgeNames.includes(rule.edges[0]));
        const compiled = compileReferenceOverlay(rule, blockId, layout.id, points, edges, overlayMoves);
        const overlay = compiled && { ...compiled, sourcePieceId: source?.id };
        if (!overlay) diagnostics.push({ severity: "warning", code: "REFERENCE_OVERLAY_UNRESOLVED", message: `${layout.id}.${rule.id} could not be drawn.` });
        return overlay ? [overlay] : [];
      });
      let bodyOverlay: DraftReferenceOverlay | undefined;
      if (layout.bodyOverlayRule) {
        const bodyEdges = layout.bodyOverlayRule.edges.map((edgeName) => edges.find((edge) => edge.id === `${blockId}.${edgeName}`));
        if (bodyEdges.every(Boolean)) bodyOverlay = { id: `${layout.id}.body`, label: layout.bodyOverlayRule.label, edges: bodyEdges as DraftEdge[] };
        else diagnostics.push({ severity: "warning", code: "BODY_OVERLAY_UNRESOLVED", message: `${layout.id} body overlay could not be drawn.` });
      }
      const internalEdgeIds = layout.internalLineNames.map((edgeId) => `${blockId}.${edgeId}`);
      blocks.push({ id: layout.id, label: layout.label, pointIds, edgeIds, internalEdgeIds, markIds, seamAllowanceIds, inlayIds, bounds, offset, printOffset: [0, 0], printRotation: 0, cutQuantity: layout.cutQuantity, grainDirection: layout.grainDirection, materialRole: layout.materialRole, cutOnFold: layout.cutOnFold, referenceOverlays, bodyOverlay });
      if (!layout.layoutWith) cursorX += Math.max(bounds.width, 150) + 100;
    }
    // Pieces drafted in the same block share one vertical datum, so matching
    // construction points (e.g. the chest line) sit level across pieces.
    const blockPieces = blocks.slice(firstPieceIndex);
    const blockMinY = Math.min(...blockPieces.map((piece) => piece.bounds.minY));
    for (const piece of blockPieces) piece.offset = [piece.offset[0], -blockMinY];
    // Overlaid pieces share their partner's offset, keeping drafting points coincident.
    for (const layout of layouts) {
      if (!layout.layoutWith) continue;
      const piece = blockPieces.find((candidate) => candidate.id === layout.id);
      const partner = blockPieces.find((candidate) => candidate.id === layout.layoutWith);
      if (piece && partner) piece.offset = [...partner.offset];
      else diagnostics.push({ severity: "warning", code: "LAYOUT_WITH_UNRESOLVED", message: `${layout.id} cannot be laid out with ${layout.layoutWith}.` });
    }
    // Start the next block after the furthest piece, including overlaid ones.
    if (blockPieces.length > 0) cursorX = Math.max(cursorX, Math.max(...blockPieces.map((piece) => piece.bounds.maxX + piece.offset[0])) + 100);
  }
  layoutPrint(blocks);
  const assertions = checkAssertions(specification.assertions ?? [], generator, points, edges);
  for (const assertion of assertions) {
    if (!assertion.passes) {
      diagnostics.push({ severity: "warning", code: "ASSERTION_FAILED", message: `${assertion.label}: ${assertion.message}` });
    }
  }

  return {
    specificationId: specification.pattern.id,
    patternName: specification.pattern.name,
    patternVersion: specification.pattern.version,
    madeFor: project.madeFor || "Default",
    printedDate: localDate(new Date()),
    mannequin: buildMannequin(project.measurements, project.bodyMeasurements, project.madeFor || "Default"),
    points,
    provenance: generator?.toProvenance() ?? {},
    edges,
    marks,
    seamAllowances,
    inlays,
    seamComparisons,
    assertions,
    blocks,
    diagnostics,
  };
}

const DEFAULT_ASSERTION_TOLERANCE = 0.5;

/** Check each spec assertion against the drafted points and edges. */
function checkAssertions(
  definitions: AssertionDefinition[],
  generator: PatternCoordinateMap | undefined,
  points: Record<string, PointTuple>,
  edges: DraftEdge[],
): DraftAssertion[] {
  return definitions.map((definition) => {
    const tolerance = definition.tolerance ?? DEFAULT_ASSERTION_TOLERANCE;
    const base = { id: definition.id, label: definition.label, source: definition.source };
    const fail = (message: string, actual?: number, expected: DraftAssertion["expected"] = { tolerance }): DraftAssertion =>
      ({ ...base, actual, expected, passes: false, message });

    let actual: number;
    if (definition.check === "distance") {
      const from = points[definition.from];
      const to = points[definition.to];
      if (!from || !to) return fail(`Point ${from ? definition.to : definition.from} was not drafted.`);
      actual = definition.axis === "x" ? Math.abs(to[0] - from[0])
        : definition.axis === "y" ? Math.abs(to[1] - from[1])
          : Math.hypot(to[0] - from[0], to[1] - from[1]);
    } else {
      const measured = definition.edges.map((edgeId) => edges.find((edge) => edge.id === edgeId));
      const missing = definition.edges.filter((_, index) => !measured[index]);
      if (missing.length > 0) return fail(`Edge ${missing.join(", ")} was not drafted.`);
      actual = measured.reduce((sum, edge) => sum + edgeLength(edge!), 0);
    }

    if (!generator) return fail("The draft failed, so expected values could not be resolved.", actual);
    const expected: DraftAssertion["expected"] = { tolerance };
    try {
      if (definition.equals !== undefined) expected.equals = generator.resolve(definition.equals);
      if (definition.minimum !== undefined) expected.minimum = generator.resolve(definition.minimum);
      if (definition.maximum !== undefined) expected.maximum = generator.resolve(definition.maximum);
    } catch (error) {
      return fail(error instanceof Error ? error.message : "Expected value could not be resolved.", actual, expected);
    }

    const problems: string[] = [];
    if (expected.equals !== undefined && Math.abs(actual - expected.equals) > tolerance) {
      problems.push(`expected ${expected.equals.toFixed(1)} ± ${tolerance} mm`);
    }
    if (expected.minimum !== undefined && actual < expected.minimum - tolerance) problems.push(`expected at least ${expected.minimum.toFixed(1)} mm`);
    if (expected.maximum !== undefined && actual > expected.maximum + tolerance) problems.push(`expected at most ${expected.maximum.toFixed(1)} mm`);
    if (problems.length > 0) return fail(`Measured ${actual.toFixed(1)} mm; ${problems.join(", ")}.`, actual, expected);
    return { ...base, actual, expected, passes: true };
  });
}

export function edgeLength(edge: DraftEdge): number {
  return edge.segments.reduce((sum, segment) => sum + segmentLength(segment), 0);
}

function localDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function centroid(points: PointTuple[]): PointTuple {
  if (points.length === 0) return [0, 0];
  const [x, y] = points.reduce(([sumX, sumY], point) => [sumX + point[0], sumY + point[1]], [0, 0]);
  return [x / points.length, y / points.length];
}

/** Build a sampled allowance strip on the side of an edge facing away from its piece. */
/** Order-preserving chain of a piece outline, each edge turned to follow the loop. */
function orientLoop(edges: DraftEdge[]): { id: string; segments: DraftSegment[] }[] | undefined {
  if (edges.length < 2) return undefined;
  const same = (a: PointTuple, b: PointTuple) => Math.hypot(a[0] - b[0], a[1] - b[1]) < 0.01;
  const first = (segments: DraftSegment[]) => segments[0].start;
  const last = (segments: DraftSegment[]) => segments[segments.length - 1].end;
  const reversed = (segments: DraftSegment[]): DraftSegment[] => [...segments].reverse().map((segment) => segment.kind === "line"
    ? { kind: "line", start: segment.end, end: segment.start }
    : { kind: "bezier", start: segment.end, control1: segment.control2, control2: segment.control1, end: segment.start });
  const next = edges[1].segments;
  let segments = edges[0].segments;
  if (!same(last(segments), first(next)) && !same(last(segments), last(next))) segments = reversed(segments);
  const loop = [{ id: edges[0].id, segments }];
  for (const edge of edges.slice(1)) {
    const end = last(loop[loop.length - 1].segments);
    const oriented = same(end, first(edge.segments)) ? edge.segments : same(end, last(edge.segments)) ? reversed(edge.segments) : undefined;
    if (!oriented) return undefined;
    loop.push({ id: edge.id, segments: oriented });
  }
  return same(last(loop[loop.length - 1].segments), first(loop[0].segments)) ? loop : undefined;
}

/** The stretch of a cut line that belongs to one outline edge. */
function runFor(runs: { sourceEdgeId: string; points: PointTuple[] }[], edgeId: string): PointTuple[] {
  return runs.filter((run) => run.sourceEdgeId === edgeId).flatMap((run, index) => index === 0 ? run.points : run.points.slice(1));
}

function segmentPolyline(segments: DraftSegment[]): PointTuple[] {
  return segments.flatMap((segment, index) => {
    const samples = sampleSegment(segment, segment.kind === "bezier" ? 32 : 1);
    return index === 0 ? samples : samples.slice(1);
  });
}

/** Fallback for outlines that do not close: an independent strip beside one edge. */
function offsetStrip(edge: DraftEdge, pieceCentre: PointTuple, distance: number, flip: boolean): PointTuple[] {
  const source = edge.segments.flatMap((segment, index) => {
    const samples = sampleSegment(segment, segment.kind === "bezier" ? 32 : 1);
    return index === 0 ? samples : samples.slice(1);
  });
  if (source.length < 2 || distance <= 0) return source;
  const midpoint = source[Math.floor(source.length / 2)];
  const before = source[Math.max(0, Math.floor(source.length / 2) - 1)];
  const after = source[Math.min(source.length - 1, Math.floor(source.length / 2) + 1)];
  const tangent = [after[0] - before[0], after[1] - before[1]];
  const candidate = normal(tangent[0], tangent[1]);
  const inferredSign = candidate[0] * (midpoint[0] - pieceCentre[0]) + candidate[1] * (midpoint[1] - pieceCentre[1]) >= 0 ? 1 : -1;
  const outwardSign = flip ? -inferredSign : inferredSign;
  const offset = source.map((point, index) => {
    const previous = source[Math.max(0, index - 1)];
    const next = source[Math.min(source.length - 1, index + 1)];
    const unit = normal(next[0] - previous[0], next[1] - previous[1]);
    return [point[0] + unit[0] * distance * outwardSign, point[1] + unit[1] * distance * outwardSign] as PointTuple;
  });
  return [...source, ...offset.reverse()];
}

function normal(dx: number, dy: number): PointTuple {
  const length = Math.hypot(dx, dy) || 1;
  return [-dy / length, dx / length];
}

function compileMark(mark: MarkDefinition, blockId: string, points: Record<string, PointTuple>, edges: DraftEdge[], options: Record<string, OptionValue>): DraftMark | undefined {
  const id = `${blockId}.${mark.id}`;
  if (mark.operation === "notch" || mark.operation === "button") {
    const at = mark.at ? points[mark.at] : undefined;
    return at ? { id, blockId, operation: mark.operation, points: [at], label: mark.label } : undefined;
  }
  if (mark.operation === "distributeButtons") {
    const edge = edges.find((candidate) => candidate.id === `${blockId}.${mark.along}`);
    const segment = edge?.segments[0];
    if (!segment) return undefined;
    const start = segment.start;
    const end = segment.end;
    const length = Math.hypot(end[0] - start[0], end[1] - start[1]);
    const count = Math.max(1, Math.round(resolveMarkValue(mark.count, options)));
    const startPadding = resolveMarkValue(mark.startPadding, options);
    const endPadding = resolveMarkValue(mark.endPadding, options);
    const usable = Math.max(0, length - startPadding - endPadding);
    const positions = Array.from({ length: count }, (_, index) => {
      const distance = startPadding + (count === 1 ? usable / 2 : usable * index / (count - 1));
      const ratio = length === 0 ? 0 : distance / length;
      return [start[0] + (end[0] - start[0]) * ratio, start[1] + (end[1] - start[1]) * ratio] as PointTuple;
    });
    return { id, blockId, operation: mark.operation, points: positions, label: mark.label };
  }
  const from = mark.from ? points[mark.from] : undefined;
  const to = mark.to ? points[mark.to] : undefined;
  return from && to ? { id, blockId, operation: mark.operation, points: [from, to], label: mark.label } : undefined;
}

/** Copy edges with a rigid move so align.from[0] lands on align.to[0], facing align.to[1]. */
function compileReferenceOverlay(rule: Omit<ReferenceOverlayDefinition, "edges"> & { edges: string[] }, blockId: string, pieceId: string, points: Record<string, PointTuple>, edges: DraftEdge[], moves: Map<string, (point: PointTuple) => PointTuple>): DraftReferenceOverlay | undefined {
  const [fromStart, fromEnd, toStart, toEnd] = [...rule.align.from, ...rule.align.to].map((id) => points[id]);
  if (!fromStart || !fromEnd || !toStart || !toEnd) return undefined;
  const followed = rule.follow ? moves.get(rule.follow) : (point: PointTuple) => point;
  if (!followed) return undefined;
  const angle = Math.atan2(toEnd[1] - toStart[1], toEnd[0] - toStart[0]) - Math.atan2(fromEnd[1] - fromStart[1], fromEnd[0] - fromStart[0]);
  const [cos, sin] = [Math.cos(angle), Math.sin(angle)];
  const move = ([x, y]: PointTuple): PointTuple => {
    const [dx, dy] = [x - fromStart[0], y - fromStart[1]];
    return followed([toStart[0] + dx * cos - dy * sin, toStart[1] + dx * sin + dy * cos]);
  };
  moves.set(rule.id, move);
  const copies: DraftEdge[] = [];
  for (const edgeName of rule.edges) {
    const edge = edges.find((candidate) => candidate.id === `${blockId}.${edgeName}`);
    if (!edge) return undefined;
    copies.push({
      ...edge,
      id: `${pieceId}.${rule.id}.${edgeName}`,
      segments: edge.segments.map((segment) => segment.kind === "line"
        ? { kind: "line", start: move(segment.start), end: move(segment.end) }
        : { kind: "bezier", start: move(segment.start), control1: move(segment.control1), control2: move(segment.control2), end: move(segment.end) }),
    });
  }
  return { id: `${pieceId}.${rule.id}`, label: rule.label, edges: copies };
}

// Each derived value that reads another (a sleeve measured from a bodice, a
// point along a portion cut at a crossing…) needs one more pass; the loop
// stops as soon as nothing changes.
const MAX_DERIVED_PASSES = 10;

function sameValues(a: Record<string, number>, b: Record<string, number>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => Object.hasOwn(b, key) && Math.abs(a[key] - b[key]) < 1e-9);
}

function resolveMarkValue(value: MarkDefinition["count"], options: Record<string, OptionValue>): number {
  if (typeof value === "number") return value;
  const option = value?.option ? options[value.option] : undefined;
  return typeof option === "number" ? option : 0;
}

function resolveGeometryDerivedValues(specification: PatternSpecification, points: Record<string, PointTuple>, generator: PatternCoordinateMap): Record<string, number> {
  const derived: Record<string, number> = {};
  const edgeDefinitions = new Map<string, EdgeDefinition>();
  for (const block of Object.values(specification.blocks)) {
    for (const [edgeId, edge] of Object.entries(block.edges ?? {})) edgeDefinitions.set(edgeId, edge);
  }

  // Compile an edge for measuring, including portions of other drafted edges.
  const blockOf = new Map(Object.entries(specification.blocks).map(([blockId, block]) => [block, blockId]));
  const compileForMeasure = (edge: EdgeDefinition, block: PatternSpecification["blocks"][string], depth = 0): DraftSegment[] => {
    if (!DERIVED_EDGE_OPERATIONS.has(edge.operation)) return compileEdge(edge, points);
    if (depth > 4) throw new Error("Derived edges nest too deeply");
    const names = edge.operation === "edgePortion" ? edge.edges ?? (edge.edge ? [edge.edge] : []) : edge.edge ? [edge.edge] : [];
    const sources = names.map((name) => {
      const source = block.edges?.[name] ?? edgeDefinitions.get(name);
      if (!source) throw new Error(`Unknown edge ${name}`);
      return { segments: compileForMeasure(source, block, depth + 1) } as DraftEdge;
    });
    const from = points[edge.from];
    const to = points[edge.to];
    if (!from || !to || sources.length === 0) throw new Error("Derived edge could not be resolved");
    const segments = derivedSegments(edge, blockOf.get(block) ?? "", sources, points, generator);
    if (!segments) throw new Error("Derived edge could not be resolved");
    return segments;
  };

  for (const block of Object.values(specification.blocks)) {
    for (const [derivedId, definition] of Object.entries(block.derivedValues ?? {})) {
      if (definition.operation === "edgeCrossing") {
        try {
          const compile = (edgeId: string) => {
            const edge = block.edges?.[edgeId] ?? edgeDefinitions.get(edgeId);
            if (!edge) throw new Error(`Unknown edge ${edgeId}`);
            return compileForMeasure(edge, block);
          };
          const chain = (definition.edges ?? []).map(compile);
          const [axis, line] = definition.x !== undefined ? ["x", definition.x] as const : ["y", definition.y!] as const;
          const [x, y] = definition.crossing ? edgesCross(chain, definition.crossing.map(compile)) : edgeCrossing(chain, axis, generator.resolve(line));
          derived[`${derivedId}X`] = x;
          derived[`${derivedId}Y`] = y;
        } catch {
          // Left unresolved: points that read it stay undrawn and are reported.
        }
        continue;
      }
      if (definition.operation === "pointAlongEdges") {
        try {
          const start = points[definition.from ?? ""];
          if (!start || definition.distance === undefined) continue;
          const chain = (definition.edges ?? []).map((edgeId) => {
            const edge = block.edges?.[edgeId] ?? edgeDefinitions.get(edgeId);
            if (!edge) throw new Error(`Unknown edge ${edgeId}`);
            return compileForMeasure(edge, block);
          });
          const [x, y] = pointAlongEdges(chain, start, generator.resolve(definition.distance));
          derived[`${derivedId}X`] = x;
          derived[`${derivedId}Y`] = y;
        } catch {
          // Left unresolved: points that read it stay undrawn and are reported.
        }
        continue;
      }
      if (definition.operation !== "edgeLengthSum") continue;
      let total = 0;
      let resolved = true;
      for (const edgeId of definition.edges ?? []) {
        const edge = block.edges?.[edgeId] ?? edgeDefinitions.get(edgeId);
        if (!edge) { resolved = false; break; }
        try {
          total += compileForMeasure(edge, block).reduce((sum, segment) => sum + segmentLength(segment), 0);
        } catch {
          resolved = false;
          break;
        }
      }
      if (resolved) derived[derivedId] = total;
    }
  }
  return derived;
}

function segmentLength(segment: import("./types").DraftSegment): number {
  const samples = sampleSegment(segment, segment.kind === "bezier" ? 80 : 1);
  return samples.slice(1).reduce((sum, point, index) => sum + Math.hypot(point[0] - samples[index][0], point[1] - samples[index][1]), 0);
}

/**
 * Cut a piece along its clip lines: the outline's cut edges become new
 * edges named `<piece>~<edge>`, the line across it `<piece>~<first line
 * edge>`, and its seam allowances and inlays follow them.
 */
function clipPiece(
  layout: { id: string; edgeNames: string[]; allowanceRules: Array<{ edges: string[]; distance: number | { option: string }; included?: boolean; flip?: boolean }>; inlayRules: Array<{ edges: string[]; distance: number | { option: string } }>; clipRules: Array<Omit<ClipDefinition, "along"> & { along: string[] }> },
  blockId: string,
  edges: DraftEdge[],
  points: Record<string, PointTuple>,
  diagnostics: Diagnostic[],
) {
  const piece = layout.id.slice(blockId.length + 1);
  const find = (name: string) => edges.find((edge) => edge.id === `${blockId}.${name}`);
  let cut = 0;
  for (const rule of layout.clipRules) {
    const outline = layout.edgeNames.map(find);
    const along = rule.along.map(find);
    const away = points[rule.away];
    const loop = outline.every((edge): edge is DraftEdge => !!edge) ? orientLoop(outline) : undefined;
    if (!loop || !along.every(Boolean) || !away) {
      diagnostics.push({ severity: "warning", code: "CLIP_UNRESOLVED", message: `${layout.id} could not be cut along ${rule.along.join(", ")}.` });
      continue;
    }
    let result;
    try {
      result = clipOutline(loop.map((edge, index) => ({ name: layout.edgeNames[index], segments: edge.segments })), along.map((edge) => edge!.segments), away);
    } catch (error) {
      diagnostics.push({ severity: "warning", code: "CLIP_INVALID", message: `${layout.id} could not be cut along ${rule.along.join(", ")}: ${error instanceof Error ? error.message : "Invalid geometry."}` });
      continue;
    }
    if (!result) continue;
    // A cut end takes the ID of a drafted point there, if any, so it shows by name.
    const pointAt = (point: PointTuple) => {
      const near = (id: string) => points[id] && Math.hypot(points[id][0] - point[0], points[id][1] - point[1]) < 0.01;
      const existing = outline.flatMap((edge) => [edge!.from, edge!.to]).concat(along.flatMap((edge) => [edge!.from, edge!.to])).find(near);
      if (existing) return existing;
      const id = `${piece}~cut${++cut}`;
      points[id] = point;
      return id;
    };
    const named = (name: string) => name.startsWith(`${piece}~`) ? name : `${piece}~${name}`;
    const store = (name: string, segments: DraftSegment[]) => {
      const id = `${blockId}.${name}`;
      const existing = edges.findIndex((edge) => edge.id === id);
      const edge: DraftEdge = { id, blockId, from: pointAt(segments[0].start), to: pointAt(segments[segments.length - 1].end), operation: "clip", segments };
      if (existing >= 0) edges[existing] = edge;
      else edges.push(edge);
    };
    const replaced = new Map<string, string[]>();
    const names: string[] = [];
    for (const edge of result.edges) {
      const name = edge.whole ? edge.name : named(edge.name);
      if (!edge.whole) store(name, edge.segments);
      replaced.set(edge.source, [...(replaced.get(edge.source) ?? []), name]);
      names.push(name);
    }
    const clipName = named(rule.along[0]);
    store(clipName, result.clip);
    names.push(clipName);
    const before = new Set(layout.edgeNames);
    const follow = (list: string[]) => list.flatMap((name) => replaced.get(name) ?? (before.has(name) ? [] : [name]));
    layout.edgeNames = names;
    layout.allowanceRules = layout.allowanceRules.map((rule) => ({ ...rule, edges: follow(rule.edges) }));
    layout.inlayRules = layout.inlayRules.map((rule) => ({ ...rule, edges: follow(rule.edges) }));
    if (rule.allowance !== undefined) layout.allowanceRules.push({ edges: [clipName], distance: rule.allowance });
  }
}
